import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hubFetch } from "../lib/auth.js";

export interface AdminRow {
  id: string;
  email: string;
  display_name: string | null;
  role: "owner" | "publisher" | "reviewer" | "viewer";
  created_at: number;
  last_login_at: number | null;
  disabled: number;
}

export function useAdmins() {
  return useQuery({
    queryKey: ["admins"],
    queryFn: async () => {
      const res = await hubFetch<{ items: AdminRow[] }>("/admin/admins");
      return res.items;
    },
  });
}

export function useCreateAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      password: string;
      role: AdminRow["role"];
      displayName?: string;
    }) =>
      hubFetch<{ id: string; email: string }>("/admin/admins", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
  });
}

export function useChangeAdminRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: AdminRow["role"] }) =>
      hubFetch(`/admin/admins/${id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
  });
}

export function useToggleAdminDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      hubFetch(`/admin/admins/${id}/${disabled ? "disable" : "enable"}`, {
        method: "PUT",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
  });
}
